import React, {
  FunctionComponent, useEffect, useState, Fragment,
  useCallback,
  useRef
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  GetRowIdParams, GridReadyEvent, IServerSideGetRowsParams, SortModelItem
} from '@ag-grid-enterprise/all-modules';
import { RootState } from 'data/root.reducers';
import { DateRange, DateRangePicker, IDateRangeShortcut } from '@blueprintjs/datetime';
import { SuppressedAlarm } from 'data/alarm/alarm.types';
import ServerSideDatasource from 'shared/commonGrid/serverSideDatasource';
import { deleteSuppressedAlarms, getSuppressedAlarms } from 'data/alarm/alarm.api';
import moment from 'moment';
import { Popover2 } from '@blueprintjs/popover2';
import {
 AnchorButton, Button, Classes, Intent
} from '@blueprintjs/core';
import i18n from 'i18n';
import { AgGridReact } from '@ag-grid-community/react';
import { suppressedAlarmsColumnDefs } from 'routes/Alarm/Alarm.types';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import getDeletedSuppressedAlarmPayload from 'routes/Alarm/utils/getDeletedSuppressedAlarmPayload';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {};

const connector = connect(mapState, mapDispatch);

const PAGE_SIZE = 100;

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const SuppressedAlarmList: FunctionComponent<Props> = ({
  history,
  isAuditEnabled,
  userId,
  userName
}: Props) => {
  const gridRef = useRef<any>(null);
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>([startOfDay, endOfDay]);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number>(0);
  const [dateRangePopupOpen, setDateRangePopupOpen] = useState<boolean>(false);
  const [dateRangeButtonText, setDateRangeButtonText] = useState<string>('Today');
  const [selectedSuppressedAlarms, setSelectedSuppressedAlarms] = useState<SuppressedAlarm[]>([]);

  const getServerSideDataSource = useCallback(() => {
    return {
      getRows(params: IServerSideGetRowsParams) {
        const { request } = params;
        const updateSortModel = request.sortModel.reduce<SortModelItem[]>((result, sortModel) => {
          if (sortModel.colId === 'timestamp') {
            result.push(
              { colId: 'date', sort: sortModel.sort },
              { colId: 'time', sort: sortModel.sort }
            )
          } else {
            result.push(sortModel);
          }
          return result;
        }, []);
        request.sortModel = updateSortModel;
        const dataSource = new ServerSideDatasource({});
        const options = dataSource.buildParamsFromJsonForXREST(request);
        const queryParams = new URLSearchParams(options);
        const body = options
        getSuppressedAlarms(body)
          .then((r) => {
            const rowData = r.data?.data || [];
            const rowCount = r.data?.metadata?.recordsInView || 0;
            params.success({ rowData, rowCount });
          })
          .catch((r) => {
            params.fail();
          });
      }
    };
  }, [selectedDateRange]);

  const buildDateRangeFilterCriteria = (
    dateRange: DateRange,
  ): string => {
    const thenextDate = new Date(); // get the next date to compare since db will compare time at 00:00
        if (dateRange[1]) {
          thenextDate.setDate(dateRange[1].getDate() + 1);
        }
        return `(datetime ge '${moment(dateRange[0]).format('YYYY-MM-DD')}') and (datetime lt '${moment(thenextDate).format('YYYY-MM-DD')}')`;
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setServerSideDatasource(getServerSideDataSource());
    }
  }, [getServerSideDataSource]);

  const onGridReady = useCallback((grid: GridReadyEvent) => {
    grid.columnApi.applyColumnState({
      state: [{ colId: 'timestamp', sort: 'desc', sortIndex: 0 }],
      defaultState: { sort: null },
    })
    grid.api.setServerSideDatasource(getServerSideDataSource());
  }, []);

  const handleShortcutChange = (shortcut: IDateRangeShortcut, index: number) => {
    setSelectedShortcutIndex(index);
    setDateRangeButtonText(shortcut.label);
  };

  const handleDateChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }
    if (gridRef.current) {
      setDateRangeButtonText(`${moment(dateRange[0]).format('lll')} - ${moment(dateRange[1]).format('lll')}`);
      setDateRangePopupOpen(false);
    }
  };

  const handleInteraction = (isOpen: boolean) => {
    setDateRangePopupOpen(isOpen);
  }

  const handleRefresh = () => {
    refreshTable();
  };

  const refreshTable = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshServerSideStore({ purge: true });
      gridRef.current.api.deselectAll();
    }
  }

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedSuppressedAlarms(selectedRows.slice());
  }

  const getRowId = (params: GetRowIdParams) => {
    return `${params.data.alarmid}-${params.data.instance}`;
  }

  const onDeleteAlarm = () => {
    deleteSuppressedAlarms(getDeletedSuppressedAlarmPayload(
      isAuditEnabled,
        userId,
        userName,
        selectedSuppressedAlarms
    ))
    .then(() => {
      refreshTable();
      setSelectedSuppressedAlarms([]);
    });
    console.log();
  }

  return (
    <div className="row alarm-table-container" style={{ height: 'inherit' }}>
      <div className="formPanel">
        <h2 className="formPanelTitle">Suppressed Alarms</h2>
      </div>

      <div className="col-md-12" style={{ height: 'inherit' }}>
        <div className="form-flex-row-css">
          {/* Flex Spacer */}
          <div className="col-sm" />
          {selectedSuppressedAlarms.length > 0 && (
            <div>
              <Button
                className="menu-button"
                text={i18n.t('common.button.deleteAlarm', { suppressCount: selectedSuppressedAlarms.length })}
                onClick={onDeleteAlarm}
              />
            </div>
          )}
          <div>
            <AnchorButton
              icon="refresh"
              intent={Intent.NONE}
              text="Refresh"
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="ag-theme-balham common-grid-container">
          <AgGridReact
            ref={gridRef}
            columnDefs={suppressedAlarmsColumnDefs}
            pagination
            paginationPageSize={PAGE_SIZE}
            rowSelection="multiple"
            rowModelType="serverSide"
            serverSideStoreType="partial"
            cacheBlockSize={PAGE_SIZE}
            onGridReady={onGridReady}
            suppressRowClickSelection
            onSelectionChanged={onSelectionChanged}
            getRowId={getRowId}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter(connector(SuppressedAlarmList));
